import { useRef, useState } from 'react';
import type { AccessListEntryType } from '@flintmint/core-shared/firestore/schemas';
import { Button } from '@flintmint/web-shared/components/button';
import { Chip } from '@flintmint/web-shared/components/chip';
import { InlineInput } from '@flintmint/web-shared/components/inline-input';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { usePageTitle } from '@/hooks/use-page-title';
import {
  addAccessListEntry,
  removeAccessListEntry,
  updateAccessListEntryComment,
  type AccessListEntry,
} from '@/services/access-list-service';
import { useAccessList } from './use-access-list';

import styles from './access-list-page.module.scss';

const normalizeValue = (type: AccessListEntryType, value: string): string => {
  const trimmed = value.trim();
  return type === 'email' ? trimmed.toLowerCase() : trimmed;
};

const validateValue = (type: AccessListEntryType, value: string, entries: AccessListEntry[]): string | null => {
  if (!value) return 'Value is required.';
  if (type === 'pattern') {
    try {
      new RegExp(value);
    } catch {
      return 'Not a valid regular expression.';
    }
  }
  if (entries.some((entry) => entry.type === type && normalizeValue(entry.type, entry.value) === value)) {
    return 'An entry with this value already exists.';
  }
  return null;
};

export const AccessListPage = () => {
  usePageTitle('Access List');
  const { entries, isLoading, error } = useAccessList();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { showToastNotification } = useToaster();

  const handleRemove = async (entry: AccessListEntry) => {
    setRemovingId(entry.id);
    try {
      await removeAccessListEntry(entry.id);
    } catch {
      showToastNotification({
        type: 'error',
        title: 'Failed to remove entry',
        description: entry.value,
      });
    } finally {
      setRemovingId(null);
    }
  };

  const emailEntries = entries.filter((entry) => entry.type === 'email');
  const patternEntries = entries.filter((entry) => entry.type === 'pattern');

  return (
    <>
      <div className={styles.page}>
        <AccessListHeader />

        <AccessListForm entries={entries} disabled={isLoading} />

        {error && <p className={styles.error}>{error}</p>}
        {isLoading ? (
          <AccessListSkeleton />
        ) : error ? null : (
          <>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Email invites</h3>
              <p className={styles.sectionNote}>Removing an entry does not affect already-admitted users.</p>
              {emailEntries.length === 0 ? (
                <p className={styles.empty}>No email invites.</p>
              ) : (
                <ul className={styles.list}>
                  {emailEntries.map((entry) => (
                    <li key={entry.id} className={styles.listItem}>
                      <span>{entry.value}</span>
                      <CommentInput entry={entry} />
                      <Chip
                        label={entry.admittedUserId ? 'Used' : 'Outstanding'}
                        color={entry.admittedUserId ? 'hero' : 'secondary'}
                      />
                      <Button
                        appearance="outline"
                        color="danger"
                        size="small"
                        disabled={removingId === entry.id}
                        onClick={() => handleRemove(entry)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Patterns</h3>
              <p className={styles.sectionNote}>Patterns are standing rules with no usage tracking.</p>
              {patternEntries.length === 0 ? (
                <p className={styles.empty}>No patterns.</p>
              ) : (
                <ul className={styles.list}>
                  {patternEntries.map((entry) => (
                    <li key={entry.id} className={styles.listItem}>
                      <code>{entry.value}</code>
                      <CommentInput entry={entry} />
                      <Button
                        appearance="outline"
                        color="danger"
                        size="small"
                        disabled={removingId === entry.id}
                        onClick={() => handleRemove(entry)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
};

export const AccessListPageSkeleton = () => (
  <div className={styles.page}>
    <AccessListHeader />
    <AccessListForm entries={[]} disabled />
    <AccessListSkeleton />
  </div>
);

const AccessListHeader = () => (
  <div className={styles.header}>
    <h2 className={styles.title}>Access List</h2>
  </div>
);

// `disabled` holds the form still while it stands in for the page; input typed there would be lost when the page mounts.
const AccessListForm = ({ entries, disabled = false }: { entries: AccessListEntry[]; disabled?: boolean }) => {
  const [type, setType] = useState<AccessListEntryType>('email');
  const [value, setValue] = useState('');
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleAdd = async () => {
    const normalized = normalizeValue(type, value);
    const validationError = validateValue(type, normalized, entries);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setIsSaving(true);
    try {
      await addAccessListEntry(type, normalized, comment.trim());
      setValue('');
      setComment('');
      setFormError(null);
    } catch (addError) {
      setFormError((addError as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={styles.addForm}>
        <select
          className={styles.typeSelect}
          value={type}
          disabled={disabled}
          onChange={(e) => {
            setType(e.target.value as AccessListEntryType);
            setFormError(null);
          }}
        >
          <option value="email">Email</option>
          <option value="pattern">Pattern</option>
        </select>
        <input
          className={styles.valueInput}
          placeholder={type === 'email' ? 'user@example.com' : 'regex pattern'}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            setValue(e.target.value);
            setFormError(null);
          }}
        />
        <input
          className={styles.commentInput}
          placeholder="Comment, e.g. (APP, Emergency)"
          value={comment}
          disabled={disabled}
          onChange={(e) => setComment(e.target.value)}
        />
        <Button color="hero" size="medium" disabled={disabled || isSaving} onClick={handleAdd}>
          Add
        </Button>
      </div>
      {formError && <p className={styles.formError}>{formError}</p>}
    </>
  );
};

const SKELETON_ROW_COUNT = 4;

const AccessListSkeleton = () => (
  <ul className={styles.list}>
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <li key={index} className={styles.listItem} data-testid="access-list-row-skeleton">
        <Skeleton width={200} height={16} />
        <span className={styles.comment}>
          <Skeleton width={160} height={16} />
        </span>
        <Skeleton width={90} height={24} />
        <Skeleton width={72} height={28} />
      </li>
    ))}
  </ul>
);

const CommentInput = ({ entry }: { entry: AccessListEntry }) => {
  const isCancelingRef = useRef(false);
  const commentAtFocusRef = useRef('');
  const { showToastNotification } = useToaster();

  const handleSave = async (input: HTMLInputElement) => {
    const trimmed = input.value.trim();
    if (trimmed === commentAtFocusRef.current) return;
    try {
      await updateAccessListEntryComment(entry.id, trimmed);
    } catch {
      showToastNotification({
        type: 'error',
        title: 'Failed to update comment',
        description: entry.value,
      });
    }
  };

  // onBlur sits on the wrapper because InlineInput's own onBlur tracks focus to re-sync from props;
  // overriding it would stop the input from reverting after Escape or picking up the saved value.
  return (
    <span
      className={styles.comment}
      onFocus={() => {
        commentAtFocusRef.current = entry.comment ?? '';
      }}
      onBlur={(e) => {
        if (isCancelingRef.current) {
          isCancelingRef.current = false;
          return;
        }
        handleSave(e.target as HTMLInputElement);
      }}
    >
      <InlineInput
        className={styles.commentField}
        value={entry.comment ?? ''}
        placeholder="Add comment"
        onKeyDown={(e) => {
          if (e.key === 'Escape') isCancelingRef.current = true;
          if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
        }}
      />
    </span>
  );
};
